import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ChecklistRounded as ActivitiesIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  DashboardOutlined as OverviewIcon,
  EuroRounded as RevenuesIcon,
  GroupsOutlined as PeopleIcon,
  Search as SearchIcon,
  SwapHorizRounded as MappingIcon,
} from '@mui/icons-material';
import PageHeader, { PageContainer } from '../components/PageHeader';
import { PILL_HEIGHT } from '../components/monthly/MultiSelectFilter';
import ProjectOverview from '../components/projects/ProjectOverview';
import ProjectActivities from '../components/projects/ProjectActivities';
import StatusChip from '../components/projects/StatusChip';
import { projectsBase, readProjectsNav } from '../components/projects/projectsNav';
import ProjectPeople from '../components/projects/ProjectPeople';
import ProjectRevenues from '../components/projects/ProjectRevenues';
import ProjectReportMapping from '../components/projects/mapping/ProjectReportMapping';
import { GREEN, GREEN_DARK, INK, LINE, MUTED } from '../components/home/homeTheme';
import { ENABLE_TAB_CONTRACT, ENABLE_TAB_MAPPING } from '../config/local.config';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { fetchProject, fetchProjectLookups, fetchProjectsList, fmtPeriod } from '../utils/projectsService';

// Schede del progetto: la scheda aperta è nel percorso (/projects/:id/:tab), Overview è quella di default
const TABS = [
  { key: 'overview', label: 'Overview', icon: <OverviewIcon /> },
  { key: 'people', label: 'People', icon: <PeopleIcon /> },
  { key: 'activities', label: 'Activities', icon: <ActivitiesIcon /> },
  ENABLE_TAB_CONTRACT && { key: 'revenues', label: 'Revenues', icon: <RevenuesIcon /> },
  ENABLE_TAB_MAPPING && { key: 'mapping', label: 'Report mapping', icon: <MappingIcon /> },
].filter(Boolean);

const tabsSx = {
  minHeight: 44,
  borderBottom: `1px solid ${LINE}`,
  mb: 2.5,
  '& .MuiTabs-indicator': { bgcolor: GREEN, height: 3, borderRadius: '3px 3px 0 0' },
  '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontSize: 14, fontWeight: 600, color: MUTED, px: 1.5, mr: 0.5, gap: 0.75 },
  '& .MuiTab-root .MuiSvgIcon-root': { fontSize: 19 },
  '& .MuiTab-root.Mui-selected': { color: GREEN_DARK },
};
const switcherSx = {
  width: 260,
  '& .MuiOutlinedInput-root': { height: PILL_HEIGHT, borderRadius: 2, fontSize: 13.5, bgcolor: 'var(--rt-paper)', pl: 1.25 },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--rt-pill-border)' },
};
const navButtonSx = { border: '1px solid var(--rt-pill-border)', borderRadius: 2, width: PILL_HEIGHT, height: PILL_HEIGHT, bgcolor: 'var(--rt-paper)' };

/**
 * Dettaglio progetto V2: intestazione con frecce precedente/successivo e selettore dei progetti,
 * schede nell'URL e avviso per le modifiche non salvate.
 */
export default function ProjectDetailV2() {
  const { id, tab: tabParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const base = projectsBase(location.pathname);
  const tab = TABS.some((t) => t.key === tabParam) ? tabParam : 'overview';
  const { canManageProjects, canDeleteProjects, isServiceManager, isTeamLeader, isJRServiceManager } = useRoleAccess();
  const { userId } = useCurrentUser();
  const ownOnly = isServiceManager || isTeamLeader || isJRServiceManager;

  // ---- Progetto ----
  const [project, setProject] = useState(null);
  const [error, setError] = useState(null);
  const loadProject = useCallback(
    () =>
      fetchProject(id).then((p) => {
        setProject(p);
        setError(null);
      }),
    [id],
  );
  useEffect(() => {
    let cancelled = false;
    setError(null);
    fetchProject(id)
      .then((p) => !cancelled && setProject(p))
      .catch((e) => !cancelled && setError(`Unable to load the project: ${e.message}`));
    return () => {
      cancelled = true;
    };
  }, [id]);

  // ---- Elenco (selettore e frecce) e dati per i form ----
  const [list, setList] = useState([]);
  const [lookups, setLookups] = useState({ customers: [], serviceTypes: [] });
  useEffect(() => {
    if (ownOnly && !userId) return undefined;
    let cancelled = false;
    fetchProjectsList({ ownOnly, userId })
      .then((projects) => !cancelled && setList([...projects].sort((a, b) => String(a.name).localeCompare(String(b.name)))))
      .catch(() => null);
    fetchProjectLookups()
      .then((l) => !cancelled && setLookups(l))
      .catch(() => null);
    return () => {
      cancelled = true;
    };
  }, [ownOnly, userId]);

  // Mentre si carica un altro progetto si mostra subito quello dell'elenco
  const listed = list.find((p) => String(p.id) === String(id));
  const current = project && String(project.id) === String(id) ? project : null;
  const shown = current || listed || null;

  // Frecce: ordine dell'elenco filtrato da cui si arriva, altrimenti alfabetico
  const nav = useMemo(() => readProjectsNav(), []);
  const order = nav?.ids?.includes(String(id)) ? nav.ids : list.map((p) => String(p.id));
  const index = order.indexOf(String(id));
  const prevId = index > 0 ? order[index - 1] : null;
  const nextId = index >= 0 && index < order.length - 1 ? order[index + 1] : null;
  const nameOf = (pid) => list.find((p) => String(p.id) === String(pid))?.name || `Project ${pid}`;
  const backTo = `${base}${nav?.search || ''}`;
  const pathFor = (pid, t = tab) => `${base}/${pid}${t === 'overview' ? '' : `/${t}`}`;

  // ---- Modifiche non salvate ----
  const [dirty, setDirty] = useState(false);
  const [pending, setPending] = useState(null);
  useEffect(() => setDirty(false), [id, tab]);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);
  const go = (path, options) => (dirty ? setPending({ path, options }) : navigate(path, options));

  // Messaggio arrivato da un'altra pagina (es. "Project created")
  const [snack, setSnack] = useState(location.state?.snack || null);
  useEffect(() => {
    if (location.state?.snack) navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const onSaved = async (message) => {
    await loadProject();
    setSnack(message);
  };

  const customer = shown && (shown.customer || lookups.customers.find((c) => String(c.id) === String(shown.customer_id)));
  const openCustomer = (cid) => go(`/customers?customer=${cid}`);

  const subtitle = (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1, rowGap: 0.5 }}>
      <Link component="button" type="button" underline="hover" onClick={() => go(backTo)} sx={{ fontSize: 13.5, fontWeight: 600, color: GREEN_DARK }}>
        Projects
      </Link>
      {shown && (
        <>
          <span>›</span>
          {shown.project_code && (
            <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12.5, color: INK }}>
              {shown.project_code}
            </Box>
          )}
          {customer && (
            <>
              <span>·</span>
              <Link component="button" type="button" underline="hover" onClick={() => openCustomer(customer.id)} sx={{ fontSize: 13.5, color: MUTED }}>
                {customer.name}
              </Link>
            </>
          )}
          <span>·</span>
          <span>{fmtPeriod(shown)}</span>
          <StatusChip active={shown.active !== false} />
        </>
      )}
    </Box>
  );

  return (
    <PageContainer>
      <PageHeader title={shown?.name || (error ? 'Project' : 'Loading…')} subtitle={subtitle}>
        <Tooltip title={prevId ? `Previous: ${nameOf(prevId)}` : ''}>
          <span>
            <IconButton size="small" disabled={!prevId} onClick={() => go(pathFor(prevId))} sx={navButtonSx} aria-label="Previous project">
              <PrevIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={nextId ? `Next: ${nameOf(nextId)}` : ''}>
          <span>
            <IconButton size="small" disabled={!nextId} onClick={() => go(pathFor(nextId))} sx={navButtonSx} aria-label="Next project">
              <NextIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Autocomplete
          size="small"
          options={list}
          value={null}
          blurOnSelect
          clearOnBlur
          getOptionLabel={(p) => p.name || ''}
          filterOptions={(options, { inputValue }) => {
            const q = inputValue.trim().toLowerCase();
            return (q ? options.filter((p) => [p.name, p.project_code].some((v) => String(v || '').toLowerCase().includes(q))) : options).slice(0, 50);
          }}
          onChange={(e, p) => p && String(p.id) !== String(id) && go(pathFor(p.id))}
          renderOption={(props, p) => (
            <li {...props} key={p.id}>
              <Box sx={{ minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: 13.5, fontWeight: String(p.id) === String(id) ? 700 : 500, color: p.active ? INK : MUTED }}>
                  {p.name}
                </Typography>
                <Typography noWrap sx={{ fontSize: 12, color: MUTED }}>
                  {[p.project_code, !p.active && 'inactive'].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Go to project…"
              InputProps={{ ...params.InputProps, startAdornment: <SearchIcon sx={{ fontSize: 18, color: MUTED, mr: 0.5 }} /> }}
            />
          )}
          noOptionsText="No projects found"
          sx={switcherSx}
        />
      </PageHeader>

      <Tabs value={tab} onChange={(e, t) => go(pathFor(id, t))} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={tabsSx}>
        {TABS.map((t) => (
          <Tab key={t.key} value={t.key} label={t.label} icon={t.icon} iconPosition="start" />
        ))}
      </Tabs>

      {error && !shown ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => go(backTo)}>
              Back to projects
            </Button>
          }
        >
          {error}
        </Alert>
      ) : !shown || (tab === 'overview' && !current) ? (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress color="success" />
        </Box>
      ) : (
        <>
          {tab === 'overview' && (
            <ProjectOverview
              key={id}
              project={current}
              customers={lookups.customers}
              serviceTypes={lookups.serviceTypes}
              userId={userId}
              canEdit={canManageProjects()}
              canDelete={canDeleteProjects()}
              onSaved={onSaved}
              onDeleted={(message) => navigate(backTo, { state: { snack: message } })}
              onDirtyChange={setDirty}
              onOpenCustomer={openCustomer}
            />
          )}
          {tab === 'activities' && <ProjectActivities key={id} projectId={id} canEdit={canManageProjects()} onDirtyChange={setDirty} />}
          {tab === 'people' && <ProjectPeople key={id} projectId={id} canEdit={canManageProjects()} />}
          {tab === 'revenues' && <ProjectRevenues key={id} project={shown} userId={userId} canEdit={canManageProjects()} onDirtyChange={setDirty} />}
          {tab === 'mapping' && <ProjectReportMapping key={id} projectId={id} onDirtyChange={setDirty} />}
        </>
      )}

      <Dialog open={!!pending} onClose={() => setPending(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">The changes on this tab haven’t been saved yet.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setPending(null)} sx={{ textTransform: 'none' }}>
            Keep editing
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              const { path, options } = pending;
              setPending(null);
              setDirty(false);
              navigate(path, options);
            }}
            sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}
          >
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)} message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </PageContainer>
  );
}
